/**
 * features/media/slots.ts
 * ------------------------------------------------------------------
 * The catalogue of image slots on the public company page.
 *
 * Work slots are DERIVED from `PROJECTS` in the landing page's data
 * file — so adding a project to the profile automatically adds its
 * upload slot here, and the admin list grows on its own. Nothing to
 * keep in sync by hand.
 *
 * Fixed slots (social card, etc.) are declared explicitly below.
 * ------------------------------------------------------------------
 */
import { PROJECTS } from '@/features/landing/work';

export interface ImageSlot {
  /** Stable key: the id under `siteContent/images` and the Storage filename. */
  id: string;
  /** Grouping header in the admin UI. */
  group: string;
  label: string;
  /** Where this image appears, so the right screenshot gets uploaded. */
  hint: string;
  /** Exact output size. Uploads are cover-cropped to this. */
  width: number;
  height: number;
  /** Fallback file under `public/` used when nothing is uploaded. */
  fallback?: string;
}

/** Browser frames on the page are 16:10. */
const WORK_W = 1600;
const WORK_H = 1000;

export const WORK_SLOTS: ImageSlot[] = PROJECTS.map((p) => ({
  id: p.id,
  group: 'Selected work',
  label: p.name,
  hint: `Browser frame in the ${p.name} section — capture the full landing page or main dashboard.`,
  width: WORK_W,
  height: WORK_H,
  fallback: `/work/${p.image}`,
}));

export const BRAND_SLOTS: ImageSlot[] = [
  {
    id: 'social-card',
    group: 'Sharing & search',
    label: 'Social share card',
    hint: 'Shown when acepslebotswana.com is shared on WhatsApp, LinkedIn or Facebook, and in Google Business posts.',
    width: 1200,
    height: 630,
    fallback: '/images/banner.jpg',
  },
];

export const ALL_SLOTS: ImageSlot[] = [...WORK_SLOTS, ...BRAND_SLOTS];

/** Distinct group names, in display order. */
export const SLOT_GROUPS: string[] = ALL_SLOTS.reduce<string[]>((acc, s) => {
  if (!acc.includes(s.group)) acc.push(s.group);
  return acc;
}, []);

export function slotById(id: string): ImageSlot | undefined {
  return ALL_SLOTS.find((s) => s.id === id);
}
